import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { useAuth } from "@/context/AuthContext";
import { brl, num } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Cog, Package, AlertTriangle, TrendingDown, DollarSign, ShoppingCart, Wrench, QrCode, Power, ArrowLeftRight, Trash2, Pencil } from "lucide-react";
import { MachineQRLabel } from "@/components/QRLabel";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { MachineForm } from "@/components/MachineForm";
import { ZoomableImage } from "@/components/ZoomableImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import api2, { apiErr } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-mono"><Icon className={`h-4 w-4 ${accent}`} /> {label}</div>
    <div className="mt-1 font-display text-2xl font-bold text-slate-50">{value}</div>
  </div>
);

export default function MachineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canFinance, can } = useAuth();
  const [m, setM] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const load = useCallback(() => { api.get(`/machines/${id}`).then((r) => setM(r.data)).catch(() => {}); }, [id]);
  useEffect(load, [load]);

  const toggleStatus = async () => {
    try { await api2.put(`/machines/${id}/status`, { ativo: m.ativo === false }); toast.success(m.ativo === false ? "Máquina reativada." : "Máquina inativada."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const handleDelete = async () => {
    try {
      const { data } = await api.get(`/machines/${id}/has-history`);
      setDel({ hasHistory: data.has_history });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      await api2.delete(`/machines/${id}`, { params: del.hasHistory ? { force: true } : {} });
      toast.success("Máquina excluída definitivamente.");
      navigate("/maquinas");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDelBusy(false); }
  };

  if (!m) return <div className="space-y-4"><Skeleton className="h-8 w-48 bg-slate-800" /><Skeleton className="h-96 bg-slate-800" /></div>;
  const r = m.resumo || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button data-testid="btn-machine-qr" variant="secondary" onClick={() => setQrOpen(true)}><QrCode className="h-4 w-4 mr-1" /> QR / Etiqueta</Button>
          {can() && <Button data-testid="btn-machine-edit" variant="secondary" onClick={() => setEditOpen(true)} className="text-sky-300"><Pencil className="h-4 w-4 mr-1" /> Editar</Button>}
          {can() && <Button data-testid="btn-machine-toggle" variant="secondary" onClick={toggleStatus} className={m.ativo === false ? "text-emerald-300" : "text-rose-300"}><Power className="h-4 w-4 mr-1" /> {m.ativo === false ? "Reativar" : "Inativar"}</Button>}
          {can() && <Button data-testid="btn-delete-machine" variant="secondary" className="text-rose-400" onClick={handleDelete}><Trash2 className="h-4 w-4 mr-1" /> Excluir</Button>}
        </div>
      </div>

      <Card className="card-metal p-0 overflow-hidden">
        <div className="grid md:grid-cols-3">
          <div className="h-48 md:h-auto bg-slate-950 overflow-hidden">
            {m.foto ? <ZoomableImage src={m.foto} alt={m.nome} testid="machine-detail-photo" className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><Cog className="h-16 w-16 text-slate-700" /></div>}
          </div>
          <div className="md:col-span-2 p-6">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-4xl font-bold text-slate-50">{m.nome}</h1>
              <span className="font-mono text-sm text-slate-950 bg-sky-400 rounded px-2 py-0.5 font-bold" data-testid="machine-equipment-id">{m.equipment_id}</span>
              {m.ativo === false && <span className="text-xs rounded bg-rose-950/60 text-rose-300 border border-rose-800/60 px-2 py-0.5">INATIVA</span>}
            </div>
            <p className="text-slate-400 text-sm">Código: {m.codigo} · DNA Digital do Equipamento (identidade central e permanente).</p>
            <p className="text-slate-400">{m.tipo} · {m.fabricante} {m.modelo} · {m.ano || "—"} · {m.localizacao}</p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat icon={Package} label="Peças cadastradas" value={num(r.total_pecas)} accent="text-sky-400" />
              <Stat icon={AlertTriangle} label="Peças críticas" value={num(r.pecas_criticas)} accent="text-rose-400" />
              <Stat icon={TrendingDown} label="Risco de falta" value={num(r.pecas_risco)} accent="text-amber-400" />
              {canFinance && <Stat icon={DollarSign} label="Valor do estoque" value={brl(r.valor_estoque)} accent="text-emerald-400" />}
              {canFinance && <Stat icon={ShoppingCart} label="Compras recomendadas" value={brl(r.valor_compras_recomendadas)} accent="text-amber-400" />}
            </div>
          </div>
        </div>
      </Card>

      <h2 className="font-display text-2xl font-semibold text-slate-100">Peças da máquina</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {m.pecas.map((p) => (
          <Card key={p.id} data-testid={`machine-part-${p.id}`} onClick={() => navigate(`/pecas/${p.id}`)} className="card-metal p-4 cursor-pointer group">
            <div className="flex gap-3">
              <div className="h-16 w-16 rounded bg-slate-950 overflow-hidden shrink-0">
                {p.foto_principal ? <ProtectedImage src={p.foto_principal} alt="" className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><Package className="h-6 w-6 text-slate-700" /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-1">
                  <div className="font-semibold text-slate-100 text-sm truncate">{p.descricao}</div>
                  <StatusBadge status={p.metrics?.status_reposicao} />
                </div>
                <div className="font-mono text-xs text-sky-400">{p.codigo_interno}</div>
                <div className="text-xs text-slate-400 mt-1">Estoque: <b className="text-slate-200">{num(p.quantidade_atual)}</b> · Instala: {p.posicao_instalacao || "—"}</div>
              </div>
            </div>
            {p.foto_instalacao && (
              <div className="mt-3"><div className="text-[11px] uppercase text-slate-500 font-mono flex items-center gap-1 mb-1"><Wrench className="h-3 w-3" /> Onde instalar</div><ProtectedImage src={p.foto_instalacao} alt="" className="w-full h-28 object-cover rounded" /></div>
            )}
          </Card>
        ))}
        {m.pecas.length === 0 && <p className="text-sm text-slate-500">Nenhuma peça associada a esta máquina.</p>}
      </div>

      <h2 className="font-display text-2xl font-semibold text-slate-100 flex items-center gap-2"><ArrowLeftRight className="h-6 w-6 text-sky-400" /> Movimentações destinadas à máquina</h2>
      <Card className="card-metal p-0 overflow-hidden">
        {(m.movimentacoes || []).length === 0 ? <div className="p-6 text-sm text-slate-500">Sem movimentações registradas para esta máquina.</div> : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono"><th className="text-left px-4 py-2">Data</th><th className="text-left px-4 py-2">Peça</th><th className="text-left px-4 py-2">Tipo</th><th className="text-right px-4 py-2">Qtd</th><th className="text-left px-4 py-2">Usuário</th></tr></thead>
            <tbody>{m.movimentacoes.map((mv) => (
              <tr key={mv.id} className="border-t border-slate-800"><td className="px-4 py-2 text-slate-400">{fmtDateTime(mv.data)}</td><td className="px-4 py-2 text-slate-200">{mv.part_desc}</td><td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${mv.tipo === "RETIRADA" ? "bg-rose-950/60 text-rose-300" : mv.tipo === "DEVOLUCAO" ? "bg-sky-950/60 text-sky-300" : "bg-slate-800 text-slate-300"}`}>{mv.tipo}</span></td><td className="px-4 py-2 text-right font-mono text-slate-200">{mv.quantidade}</td><td className="px-4 py-2 text-slate-400">{mv.usuario_nome}</td></tr>
            ))}</tbody>
          </table>
          </div>
        )}
      </Card>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-display text-2xl">Etiqueta da Máquina</DialogTitle></DialogHeader>
          <MachineQRLabel machine={m} />
          <p className="text-xs text-slate-500 text-center">Escaneie para abrir o DNA Digital do equipamento ({m.equipment_id}).</p>
        </DialogContent>
      </Dialog>
      <ForceDeleteDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} entityLabel="a máquina" name={m.nome} hasHistory={del?.hasHistory} busy={delBusy} onConfirm={confirmDelete} />
      <MachineForm open={editOpen} onOpenChange={setEditOpen} machine={m} onSaved={load} />
    </div>
  );
}
